import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";

export const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-[#FF6B00] hover:text-[#E65C00] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-black text-[#1A1C19] mb-8" style={{ fontFamily: "Fraunces, serif" }}>
          Refund Policy
        </h1>

        {/* Guarantee Badge */}
        <div className="bg-[#E8F5E9] border-2 border-[#2E7D32] rounded-2xl p-6 mb-10 text-center">
          <CheckCircle size={48} className="text-[#2E7D32] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#2E7D32] mb-2">180-Day Money Back Guarantee</h2>
          <p className="text-[#4A5043]">
            We stand behind our products with a full 180-day satisfaction guarantee. 
            If you're not completely satisfied, we'll refund your purchase – no questions asked.
          </p>
        </div>
        
        <div className="prose prose-lg max-w-none text-[#4A5043]">
          <p className="text-sm text-gray-500 mb-8">Last Updated: February 2025</p>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Our Satisfaction Guarantee</h2>
            <p className="mb-4">
              We are confident in the quality of our products. That's why we offer a comprehensive 180-day 
              money-back guarantee. If for any reason you are not satisfied with your purchase, you can 
              request a full refund within 180 days from the date of purchase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">How to Request a Refund</h2>
            <p className="mb-4">To request a refund, please follow these simple steps:</p>
            <ol className="list-decimal pl-6 mb-4 space-y-3">
              <li>
                <strong>Contact Customer Support:</strong> Reach out to our customer support team via our 
                <Link to="/contact" className="text-[#FF6B00] hover:underline ml-1">Contact Page</Link> or 
                by email with your order number and reason for the refund request.
              </li>
              <li>
                <strong>Receive Return Instructions:</strong> Our team will provide you with return instructions 
                and a return authorization number within 24-48 business hours.
              </li>
              <li>
                <strong>Return the Product:</strong> Ship the product back to us using the provided instructions. 
                You may return opened or unopened bottles.
              </li>
              <li>
                <strong>Receive Your Refund:</strong> Once we receive your return, we will process your refund 
                within 5-7 business days. The refund will be credited to your original payment method.
              </li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Refund Eligibility</h2>
            <p className="mb-4">To be eligible for a refund:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>The refund request must be made within 180 days of the original purchase date</li>
              <li>You must provide your order number or proof of purchase</li>
              <li>Products should be returned to the address provided by our customer support team</li>
              <li>Both opened and unopened products are eligible for refund</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Refund Processing Time</h2>
            <p className="mb-4">
              Once your return is received and inspected, we will send you an email to notify you that we 
              have received your returned item. We will also notify you of the approval or rejection of your 
              refund.
            </p>
            <p className="mb-4">
              If approved, your refund will be processed, and a credit will automatically be applied to your 
              original method of payment within 5-7 business days. Please note that depending on your financial 
              institution, it may take an additional 2-10 business days for the credit to appear on your statement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Shipping Costs</h2>
            <p className="mb-4">
              Original shipping costs are non-refundable. If you receive a refund, the cost of return shipping 
              will be deducted from your refund unless the return is due to our error (e.g., you received an 
              incorrect or defective item).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Exchanges</h2>
            <p className="mb-4">
              We only replace items if they are defective or damaged. If you need to exchange it for the same 
              item, please contact us through our 
              <Link to="/contact" className="text-[#FF6B00] hover:underline ml-1">Contact Page</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Late or Missing Refunds</h2>
            <p className="mb-4">If you haven't received a refund yet:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>First, check your bank account again</li>
              <li>Contact your credit card company – it may take some time before your refund is officially posted</li>
              <li>Contact your bank – there is often some processing time before a refund is posted</li>
              <li>If you've done all of this and still have not received your refund, please contact us</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-[#1A1C19] mb-4">Contact Us</h2>
            <p className="mb-4">
              If you have any questions about our refund policy, please contact us:
            </p>
            <p className="mb-4">
              <Link to="/contact" className="text-[#FF6B00] hover:underline">Visit our Contact Page</Link>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1C19] text-white py-6 text-center text-sm">
        <p>© {new Date().getFullYear()} All rights reserved.</p>
      </footer>
    </div>
  );
};

export default RefundPolicy;
